import assign from 'lodash/assign';
import merge from 'lodash/merge';
import { useEffect, useMemo, useState } from 'react';
import {
  getFrameRxValues,
  getLensesDataContent,
  getLensesDataPackages,
  getLensesDataPrices,
} from '../backend/api';
import { env, getChannelFromBrand, sunRule } from '../utils/functions';
import { useManifestGenerator } from './useManifestGenerator';
import { LensPackage } from '../meta/IBrain';

const getRxValues = (locale, rxValues) => {
  if (!rxValues[locale]) {
    const defaultRxValues = rxValues['en_US'];
    if (defaultRxValues) {
      return defaultRxValues;
    } else {
      throw new Error('no rxValues for en_US');
    }
  } else {
    return rxValues[locale];
  }
};

const isPublished = (locale, published) => {
  if (!published[locale] && typeof published[locale] !== 'boolean') {
    const defaultRxValues = published['en_US'];
    if (defaultRxValues === true || defaultRxValues === false) {
      return defaultRxValues;
    } else {
      throw new Error('no published for en_US');
    }
  } else {
    return published[locale];
  }
};

const noPricesInPackages = (packages) => {
  if (!packages) return null;
  const onlyLensPackages = packages.find((pkg) => pkg?.frame)
    ? packages.map((pkg) => pkg.lensPackage)
    : packages;
  return onlyLensPackages.every((pkg) => {
    const keys = Object.keys(pkg);
    return !keys.includes('offerPrice') && !keys.includes('listPrice');
  });
};

const mergeLensesDataPrices = (packages, prices) => {
  return packages
    .filter(({ lensPackage }) => {
      return prices.find((p) => p.lensPackage.upc === lensPackage.upc);
    })
    .map(({ frame, lensPackage }) => {
      const pkg = prices.find((p) => p.lensPackage.upc === lensPackage.upc);
      return {
        frame: {
          ...frame,
          ...(pkg?.frame ? pkg.frame : {}),
        },
        lensPackage: {
          ...assign({}, pkg?.lensPackage, lensPackage),
          offerPrice: pkg?.lensPackage.offerPrice,
          listPrice: pkg?.lensPackage.listPrice,
        },
      };
    });
};

const mergeAkamaiPrices = (packages, prices) => {
  return packages
    .filter(({ lensPackage }) => {
      return prices.prices.find((p) => p.lensPackage.upc === lensPackage.upc);
    })
    .map(({ frame, lensPackage }) => {
      const pkg = prices.prices.find(
        (p) => p.lensPackage.upc === lensPackage.upc
      );
      return {
        frame: {
          ...frame,
        },
        lensPackage: {
          ...assign({}, pkg?.lensPackage, lensPackage),
          services: merge(lensPackage.services, pkg?.lensPackage.services),
          offerPrice: pkg?.lensPackage.offerPrice,
          listPrice: pkg?.lensPackage.listPrice,
        },
      };
    });
};

export function useBackend(config: any, backofficeConfig: any) {
  const [packages, setPackages] = useState(config?.lensesData?.packages);
  const [prices, setPrices] = useState(config?.lensesData?.prices);
  const [content, setContent] = useState(config?.lensesData?.content);
  const [frameRxValues, setFrameRxValues] = useState(
    config?.data?.frame?.rxValues
  );
  const [newConfig, setNewConfig] = useState(config);
  const [isLoading, setIsLoading] = useState(true);

  // get frame imgeUrl / color / model from akamai if missing
  const frame =
    config?.data?.frame ?? useManifestGenerator(config, backofficeConfig);
  const flowCategory = backofficeConfig?.definingAttributes?.category;
  const baseURL =
    backofficeConfig?.definingAttributes?.assetsCDN ||
    config?.baseURLs?.assetsCDN ||
    'https://assets.lenscrafters.com';
  const frameModel = config?.data?.frame?.model;
  const frameColor = (config?.data?.frame?.color ?? '').replace(/\//g, '_');
  const channel = getChannelFromBrand(config?.brand);
  const locale = config?.translation?.language || 'en_US';
  const country =
    config?.data?.country ||
    config?.translation?.language?.split('_')?.[1] ||
    'US';
  const environment = env(); //process.env.REACT_APP_ENVIRONMENT_URL;

  useEffect(() => {
    let category;
    if (flowCategory && flowCategory?.toLowerCase() !== 'mixandmatch') {
      category = flowCategory.toUpperCase();
    } else if (packages && packages.length) {
      const pkgs = packages?.filter(
        (pkg: any) => pkg.lensPackage?.type?.toLowerCase() !== 'frame only'
      );
      category =
        (pkgs?.every((p) => sunRule(p.lensPackage)) && 'SUNGLASSES') ||
        'EYEGLASSES';
    }
    if (!content && category) {
      const contentUrl = `${baseURL}/extra/image/rxcbe/${
        environment || 'test'
      }/pof/${channel}/pof_${channel}_${category}_${locale}.json.zip`;
      getLensesDataContent(contentUrl, setContent);
    }
  }, [content, packages]);

  useEffect(() => {
    if (packages && packages.length > 0) {
      if (noPricesInPackages(packages) && !config?.lensesData?.prices) {
        getLensesDataPrices(URLS.prices, setPrices);
      }
    }
  }, [packages]);

  const URLS = {
    prices: `${baseURL}/extra/image/rxcbe/${
      environment || 'test'
    }/prices/${channel}/prices_${country}.json`,
    packages: `${baseURL}/extra/image/rxcbe/${
      environment || 'test'
    }/packages/${channel}/package_${frameModel}.json.zip`,
    powers: `${baseURL}/extra/image/rxcbe/${
      environment || 'test'
    }/powers/${channel}/power_range_${frameModel}.json`,
  };

  window.rxcBrain.URLS = URLS;

  useEffect(() => {
    const setLensesDataPackages = (data: any) => {
      console.log(data);
      const moco = `${frameModel}__${frameColor}`;
      const lensesIds = data.frames[moco];
      let packages = Object.keys(data.packages)
        .filter((lensId) => lensesIds.includes(lensId))
        .reduce((acc: LensPackage[], lensId: string) => {
          const lensPkgs = data.packages[lensId].map((pkg: LensPackage) => ({
            frame: {
              offerPrice: config.data.frame.offerPrice,
              insPrice: null,
            },
            lensPackage: pkg,
          }));
          return [...acc, ...lensPkgs];
        }, [])
        .filter((pkg) => isPublished(locale, pkg.lensPackage.published));

      packages.forEach((pkg) => {
        pkg.lensPackage.rxValues = getRxValues(
          locale,
          pkg.lensPackage.rxValues
        );
      });

      console.log(packages);
      setPackages(packages);
    };

    const setAllFramePowerRanges = (data) => {
      const moco = `${frameModel}__${frameColor}`;
      const currentFrame = data.find((frame) => frame.moco === moco);
      setFrameRxValues(getRxValues(locale, currentFrame.rxValues));
    };

    if (config && !config?.lensesData?.packages) {
      console.log('packages from akamai');
      getLensesDataPackages(URLS.packages, setLensesDataPackages);
    }
    if (config?.lensesData?.prices?.length === 0) {
      console.error(
        '❌ ERROR!%c RXC receives an incorrect configuration: lensesData.prices is an empty array.'
      );
    }
    if (!config?.lensesData?.prices) {
      if (
        config?.lensesData?.packages &&
        noPricesInPackages(config?.lensesData?.packages)
      ) {
        getLensesDataPrices(URLS.prices, setPrices);
      }
    }
    if (!config.data.frame?.rxValues) {
      getFrameRxValues(URLS.powers, setAllFramePowerRanges);
    }
  }, []);

  const lensPackagesWithPrices = useMemo(() => {
    if (!packages) return null;
    if (!noPricesInPackages(packages)) {
      return packages;
    }
    if (prices) {
      console.log('no prices in lensesData.packages');
      if (Array.isArray(prices)) {
        console.log('prices from lensesData.prices');
        console.log(
          'RXC3 | merged lenses with prices',
          mergeLensesDataPrices(packages, prices)
        );
        return mergeLensesDataPrices(packages, prices);
      } else {
        console.log('prices from akamai');
        console.log(
          'RXC3 | merged lenses with akamai prices',
          mergeAkamaiPrices(packages, prices)
        );
        return mergeAkamaiPrices(packages, prices);
      }
    }
    return null;
  }, [packages, prices]);

  useEffect(() => {
    if (lensPackagesWithPrices && content && frameRxValues && frame) {
      const newContent = content?.content;
      let newConfig;
      if (newContent) {
        newConfig = {
          ...config,
          data: {
            ...config.data,
            frame: {
              ...frame,
              rxValues: frameRxValues,
            },
          },
          lensesData: {
            ...config?.lensesData,
            packages: lensPackagesWithPrices,
            content: { ...newContent, badge: null },
          },
        };
      } else {
        newConfig = {
          ...config,
          data: {
            ...config.data,
            frame: {
              ...frame,
              rxValues: frameRxValues,
            },
          },
          lensesData: {
            ...config?.lensesData,
            packages: lensPackagesWithPrices,
            content: { ...content, badge: null },
          },
        };
      }
      setNewConfig(newConfig);
      setIsLoading(false);
    }
  }, [lensPackagesWithPrices, content, frameRxValues, frame]);

  if (isLoading || !config) return null;

  return newConfig;
}
